import { motion } from 'framer-motion'
import { Link, Routes, Route } from 'react-router-dom'
import { BookOpen, LifeBuoy, ArrowRight } from 'lucide-react'
import { navigationData } from '../utils/navigation'

const trainingCategories = [
  {
    icon: BookOpen,
    title: 'Documentation',
    description: 'Technical guides for running and building on Graffiti™ OS.',
    href: '/training/resources/docs',
    gradient: 'from-signal-500 to-synth-600',
  },
  {
    icon: LifeBuoy,
    title: 'Work With Our Team',
    description: "Onboarding and hands-on training from the people who built this. We're a small team, so you'll talk to a real person, not a help desk.",
    href: '/support/contact/sales',
    gradient: 'from-ember-500 to-synth-600',
  },
]

function TrainingIndex() {
  return (
    <div className="pt-24 pb-16">
      {/* Hero */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-synth-950/30 to-void-950" />
        <div className="absolute inset-0 grid-overlay opacity-30" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold text-void-50 mb-6">
              Training & <span className="text-gradient">Onboarding</span>
            </h1>
            <p className="text-xl text-void-400 max-w-2xl mx-auto">
              Direct, hands-on help getting set up — not a self-serve course catalog.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {trainingCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={category.href}
                  className="group block h-full p-8 rounded-3xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${category.gradient} mb-6`}>
                    <category.icon className="w-8 h-8 text-white" />
                  </div>

                  <h2 className="text-2xl font-display font-bold text-void-50 mb-3 group-hover:text-synth-300 transition-colors">
                    {category.title}
                  </h2>
                  <p className="text-void-400">{category.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Talk to us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-synth-900/50 to-void-900 border border-synth-800/30 text-center"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-synth-500/20 text-synth-300 text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              Training, Not a Course Catalog
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-void-50 mb-4 max-w-2xl mx-auto">
              We'll walk you through it directly
            </h2>
            <p className="text-void-400 mb-6 leading-relaxed max-w-xl mx-auto">
              We're too small a team to run a certification program, and we'd rather not fake one.
              If you or your organization want help getting set up with Graffiti™ or 23Controls™,
              talk to us — training is something we sell, delivered by the people who built it.
            </p>
            <Link to="/support/contact/sales" className="btn-primary inline-flex items-center gap-2">
              Talk to Our Team
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

function CategoryPage({ category }) {
  const data = navigationData.training?.categories?.find(c => c.href.includes(category))
  
  if (!data) return <TrainingIndex />
  
  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-4">
              {data.name}
            </h1>
            <p className="text-xl text-void-400 mb-12">Explore our {data.name.toLowerCase()} offerings</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {data.items.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className="block p-6 rounded-2xl bg-void-900/50 border border-void-800/50 hover:border-synth-700/50 transition-all group"
                  >
                    <h3 className="text-lg font-display font-semibold text-void-100 mb-2 group-hover:text-synth-300 transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-void-400 text-sm">{item.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default function TrainingPage() {
  return (
    <Routes>
      <Route index element={<TrainingIndex />} />
      <Route path="certifications/*" element={<CategoryPage category="certifications" />} />
      <Route path="courses/*" element={<CategoryPage category="courses" />} />
      <Route path="resources/*" element={<CategoryPage category="resources" />} />
    </Routes>
  )
}
